import { withSessionToken } from '../../auth';
import { API_URL } from '../../config';

export type AuthHeadersFn = (json?: boolean) => HeadersInit;

async function fetchJson<T>(url: string, headers: HeadersInit): Promise<T> {
  const response = await fetch(url, { headers });
  if (!response.ok) {
    const err = await response.json().catch(() => ({ detail: `HTTP ${response.status}` }));
    throw new Error(err.detail || `Request failed: ${response.status}`);
  }
  return response.json();
}

async function mutate<T>(url: string, method: string, headers: HeadersInit, body?: unknown): Promise<T> {
  const response = await fetch(url, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({ detail: `HTTP ${response.status}` }));
    throw new Error(err.detail || `Request failed: ${response.status}`);
  }
  return response.json();
}

export interface MqttStatus {
  configured: boolean;
  connected: boolean;
  broker: string;
  port: number;
  device_count: number;
  online_count: number;
}

export interface MqttDevice {
  sn: string;
  device_type: number;
  device_sub_type: number;
  gateway_sn: string | null;
  callsign: string | null;
  model_name: string | null;
  firmware_version: string | null;
  is_online: boolean;
  first_seen_at: string;
  last_seen_at: string;
}

export interface MqttDeviceState {
  device_sn: string;
  is_online: boolean;
  last_seen_at: string | null;
  state: Record<string, unknown>;
}

export interface HmsAlert {
  device_sn: string;
  timestamp: string;
  code: string;
  level: number;
  description: string;
}

export interface CommandResult {
  result: {
    bid: string;
    method: string;
    result: number;
    data: Record<string, unknown>;
  };
}

export async function fetchMqttStatus(h: AuthHeadersFn): Promise<MqttStatus> {
  return fetchJson(`${API_URL}/api/mqtt/status`, h());
}

export async function fetchMqttDevices(h: AuthHeadersFn): Promise<{ devices: MqttDevice[] }> {
  return fetchJson(`${API_URL}/api/mqtt/devices`, h());
}

export async function fetchDeviceState(h: AuthHeadersFn, sn: string): Promise<MqttDeviceState> {
  return fetchJson(`${API_URL}/api/mqtt/devices/${sn}/state`, h());
}

export async function fetchDeviceHms(h: AuthHeadersFn, sn: string): Promise<{ alerts: HmsAlert[] }> {
  return fetchJson(`${API_URL}/api/mqtt/devices/${sn}/hms`, h());
}

export async function fetchAllHms(h: AuthHeadersFn): Promise<{ alerts: HmsAlert[] }> {
  return fetchJson(`${API_URL}/api/mqtt/hms`, h());
}

export async function sendDeviceCommand(h: AuthHeadersFn, sn: string, command: string): Promise<CommandResult> {
  return mutate(`${API_URL}/api/mqtt/devices/${sn}/command`, 'POST', h(true), { command });
}

export interface ConnectionLogEntry {
  id?: number;
  event_type: string;
  device_sn: string;
  detail: string;
  created_at?: string;
  timestamp?: string;
}

export async function fetchConnectionLog(h: AuthHeadersFn, limit = 100): Promise<{ entries: ConnectionLogEntry[] }> {
  return fetchJson(`${API_URL}/api/mqtt/connection-log?limit=${limit}`, h());
}

export interface LivestreamResult {
  result: Record<string, unknown>;
  stream_active: boolean;
  hls_url?: string;
  device_sn?: string;
}

/**
 * Start a livestream on an aircraft.
 *
 * `videoQuality` is OPTIONAL and the key is OMITTED when it is not given. It
 * must stay that way: 0 is a real tier (adaptive), so a default of 0 sends
 * "the operator asked for adaptive" on every call — and an explicit request
 * always wins over the deployment default, by design.
 *
 * That is exactly how `DJI_LIVE_VIDEO_QUALITY` came to have no effect on any
 * deployment: `.env.demo` asked for `smooth`, the backend logged
 * `video_quality=0 (adaptive)`, and the aircraft went on pushing 1280x720 into
 * a grid of 640x360 tiles. Nothing failed; the knob was simply never consulted.
 *
 * The backend already models this correctly — `MQTTLivestreamStart.video_quality`
 * is `Optional[int] = None` precisely so "give me adaptive" can be told apart
 * from "I did not say". This side has to agree or that Optional buys nothing.
 */
export async function startLivestream(
  h: AuthHeadersFn, sn: string, videoQuality?: number,
): Promise<LivestreamResult> {
  return mutate(
    `${API_URL}/api/mqtt/devices/${sn}/livestream/start`, 'POST', h(true),
    videoQuality === undefined ? {} : { video_quality: videoQuality },
  );
}

export async function stopLivestream(h: AuthHeadersFn, sn: string): Promise<LivestreamResult> {
  return mutate(`${API_URL}/api/mqtt/devices/${sn}/livestream/stop`, 'POST', h(true));
}

export interface ActiveStream {
  device_sn: string;
  hls_url: string;
  display_name: string;
}

export async function fetchActiveStreams(h: AuthHeadersFn): Promise<{ streams: ActiveStream[] }> {
  return fetchJson(`${API_URL}/api/mqtt/streams`, h());
}

export async function sendFlightTask(h: AuthHeadersFn, sn: string, flightId: string): Promise<CommandResult> {
  return mutate(`${API_URL}/api/mqtt/devices/${sn}/flight-task`, 'POST', h(true), {
    wayline_url: flightId,
    task_type: 'immediate',
  });
}

export async function fetchFeedToken(h: AuthHeadersFn): Promise<{ token: string; expires_in: number }> {
  return fetchJson(`${API_URL}/api/drone/feed-token`, h());
}

export function buildDroneWsUrl(_h: AuthHeadersFn): string {
  // A WebSocket cannot send a header. This used to read X-API-Key back out of the auth
  // headers, which is what kept the shared key in the bundle; it carries the session now.
  const wsBase = API_URL.replace('http://', 'ws://').replace('https://', 'wss://');
  return withSessionToken(`${wsBase}/ws/drone`);
}

// ── Device Removal ─────────────────────────────────────────────────

export async function removeDevice(h: AuthHeadersFn, sn: string): Promise<{ removed: string[]; blocked: string[] }> {
  return mutate(`${API_URL}/api/mqtt/devices/${sn}`, 'DELETE', h(true));
}

// ── Telemetry History ───────────────────────────────────────────────

export interface TelemetryPoint {
  ts: string;
  latitude: number | null;
  longitude: number | null;
  height: number | null;
  altitude: number | null;
  horizontal_speed: number | null;
  vertical_speed: number | null;
  wind_speed: number | null;
  heading: number | null;
  home_distance: number | null;
  battery_percent: number | null;
  mode_code: number | null;
  elevation: number | null;
}

export interface TelemetryHistoryResponse {
  device_sn: string;
  count: number;
  points: TelemetryPoint[];
}

export interface TelemetrySummaryResponse {
  device_sn: string;
  count: number;
  first_ts: string | null;
  last_ts: string | null;
}

export async function fetchTelemetryHistory(
  h: AuthHeadersFn,
  sn: string,
  start: string,
  end?: string,
  limit?: number,
): Promise<TelemetryHistoryResponse> {
  const params = new URLSearchParams({ start });
  if (end) params.set('end', end);
  if (limit) params.set('limit', String(limit));
  return fetchJson(`${API_URL}/api/telemetry/devices/${sn}/history?${params}`, h());
}

export async function fetchTelemetrySummary(
  h: AuthHeadersFn,
  sn: string,
): Promise<TelemetrySummaryResponse> {
  return fetchJson(`${API_URL}/api/telemetry/devices/${sn}/summary`, h());
}

export function buildTelemetryCsvUrl(_h: AuthHeadersFn, sn: string, start: string, end?: string): string {
  // Downloaded by navigating to the URL, so no header — same reason as the socket above.
  const params = new URLSearchParams({ start });
  if (end) params.set('end', end);
  return withSessionToken(`${API_URL}/api/telemetry/devices/${sn}/export.csv?${params}`);
}

export interface PilotPairing {
  code: string;
  expires_in: number;
}

/** A one-time code the pilot types into the DJI Pilot 2 H5 page (`/api/pilot/login`).
 *  That page carries no credentials; it receives them only in exchange for this code. */
export async function createPilotPairing(h: AuthHeadersFn): Promise<PilotPairing> {
  return mutate(`${API_URL}/api/pilot/pairing`, 'POST', h(true));
}

export type PilotPairingState =
  | { state: 'pending'; expires_in: number }
  | { state: 'paired'; paired_at: number }
  | { state: 'unknown' };

/** What became of a code this operator issued: still pending, used by an RC, or gone. */
export async function fetchPilotPairingStatus(h: AuthHeadersFn, code: string): Promise<PilotPairingState> {
  return fetchJson(`${API_URL}/api/pilot/pairing/${encodeURIComponent(code)}`, h());
}

/** "9:05" for 545 seconds; "0:00" once expired. */
export function formatCountdown(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}
